import { cs } from '../../lib/currency';
import { useEffect, useState } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { 
  AlertDialog, 
  AlertDialogAction, 
  AlertDialogCancel, 
  AlertDialogContent, 
  AlertDialogDescription, 
  AlertDialogFooter, 
  AlertDialogHeader, 
  AlertDialogTitle 
} from '../../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import DataTable from '../../components/DataTable';
import api from '../../services/api';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Tag, Percent, DollarSign, Eye, Copy, Check } from 'lucide-react';

const Discounts = () => {
  const [discounts, setDiscounts] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [discountToDelete, setDiscountToDelete] = useState(null);
  const [editingDiscount, setEditingDiscount] = useState(null);
  
  // Usage tracking state
  const [usageDialogOpen, setUsageDialogOpen] = useState(false);
  const [selectedDiscountForUsage, setSelectedDiscountForUsage] = useState(null);
  const [usageRecords, setUsageRecords] = useState([]);
  const [loadingUsage, setLoadingUsage] = useState(false);
  const [copiedCode, setCopiedCode] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    discount_type: 'percentage',
    value: '',
    application: 'one_time',
    is_active: true,
    min_enrollment_months: '',
    applicable_programs: [],
    max_uses: '',
    valid_from: '',
    valid_until: ''
  });

  const fetchData = async () => {
    try {
      const [discountsRes, programsRes] = await Promise.all([
        api.get('/discounts'),
        api.get('/programs')
      ]);
      setDiscounts(discountsRes.data);
      setPrograms(programsRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const resetForm = () => {
    setFormData({
      name: '',
      code: '',
      description: '',
      discount_type: 'percentage',
      value: '',
      application: 'one_time',
      is_active: true,
      min_enrollment_months: '',
      applicable_programs: [],
      max_uses: '',
      valid_from: '',
      valid_until: ''
    });
    setEditingDiscount(null);
  };

  const handleEdit = (discount) => {
    setEditingDiscount(discount);
    setFormData({
      name: discount.name,
      code: discount.code || '',
      description: discount.description || '',
      discount_type: discount.discount_type,
      value: discount.value.toString(),
      application: discount.application,
      is_active: discount.is_active,
      min_enrollment_months: discount.min_enrollment_months?.toString() || '',
      applicable_programs: discount.applicable_programs || [],
      max_uses: discount.max_uses?.toString() || '',
      valid_from: discount.valid_from || '',
      valid_until: discount.valid_until || ''
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        name: formData.name,
        code: formData.code || null,
        description: formData.description || null,
        discount_type: formData.discount_type,
        value: parseFloat(formData.value),
        application: formData.application,
        is_active: formData.is_active,
        min_enrollment_months: formData.min_enrollment_months ? parseInt(formData.min_enrollment_months) : null,
        applicable_programs: formData.applicable_programs.length > 0 ? formData.applicable_programs : null,
        max_uses: formData.max_uses ? parseInt(formData.max_uses) : null,
        valid_from: formData.valid_from || null,
        valid_until: formData.valid_until || null
      };

      if (editingDiscount) {
        await api.put(`/discounts/${editingDiscount.id}`, submitData);
        toast.success('Discount updated successfully');
      } else {
        await api.post('/discounts', submitData);
        toast.success('Discount created successfully');
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save discount');
    }
  };

  const handleDelete = (discount) => {
    setDiscountToDelete(discount);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!discountToDelete) return;
    try {
      await api.delete(`/discounts/${discountToDelete.id}`);
      toast.success('Discount deleted successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete discount');
    } finally {
      setDeleteDialogOpen(false);
      setDiscountToDelete(null);
    }
  };

  const toggleProgramSelection = (programId) => {
    const current = formData.applicable_programs || [];
    if (current.includes(programId)) {
      setFormData({
        ...formData,
        applicable_programs: current.filter(id => id !== programId)
      });
    } else {
      setFormData({
        ...formData,
        applicable_programs: [...current, programId]
      });
    }
  };

  const viewUsage = async (discount) => {
    setSelectedDiscountForUsage(discount);
    setUsageDialogOpen(true);
    setLoadingUsage(true);
    try {
      const response = await api.get(`/discounts/${discount.id}/usage`);
      setUsageRecords(response.data || []);
    } catch (error) {
      toast.error('Failed to load usage history');
      setUsageRecords([]);
    } finally {
      setLoadingUsage(false);
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    toast.success('Code copied to clipboard');
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const columns = [
    {
      key: 'code',
      label: 'Code',
      sortable: true,
      filterable: true,
      filterType: 'text',
      render: (row) => (
        row.code ? (
          <div className="flex items-center gap-2">
            <button
              onClick={() => viewUsage(row)}
              className="font-mono text-sm bg-slate-100 hover:bg-slate-200 px-2 py-1 rounded text-blue-600 hover:text-blue-700 transition-colors cursor-pointer"
              data-testid={`view-usage-${row.id}`}
            >
              {row.code}
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); copyCode(row.code); }}
              className="text-slate-400 hover:text-slate-600"
              title="Copy code"
            >
              {copiedCode === row.code ? (
                <Check className="h-4 w-4 text-green-500" />
              ) : (
                <Copy className="h-4 w-4" />
              )}
            </button>
          </div>
        ) : (
          <span className="text-slate-400 text-sm">No code</span>
        )
      )
    },
    {
      key: 'name',
      label: 'Discount Name',
      sortable: true,
      filterable: true,
      filterType: 'text',
      render: (row) => (
        <div>
          <p className="font-medium text-slate-900">{row.name}</p>
          {row.description && (
            <p className="text-sm text-slate-500 truncate max-w-xs">{row.description}</p>
          )}
        </div>
      )
    },
    {
      key: 'discount_type',
      label: 'Type',
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: [
        { value: 'percentage', label: 'Percentage' },
        { value: 'fixed', label: 'Fixed Amount' }
      ],
      render: (row) => (
        <div className="flex items-center gap-2">
          {row.discount_type === 'percentage' ? (
            <Percent className="h-4 w-4 text-blue-500" />
          ) : (
            <DollarSign className="h-4 w-4 text-green-500" />
          )}
          <span className="font-medium">
            {row.discount_type === 'percentage' ? `${row.value}%` : `${cs()}${row.value}`}
          </span>
        </div>
      )
    },
    {
      key: 'application',
      label: 'Application',
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: [
        { value: 'one_time', label: 'One-Time' },
        { value: 'recurring', label: 'Recurring' }
      ],
      render: (row) => (
        <Badge className={row.application === 'recurring' ? 'bg-purple-100 text-purple-700' : 'bg-slate-100 text-slate-700'}>
          {row.application === 'recurring' ? 'Recurring' : 'One-Time'}
        </Badge>
      )
    },
    {
      key: 'times_used',
      label: 'Usage',
      sortable: true,
      render: (row) => (
        <button
          onClick={() => viewUsage(row)}
          className="text-sm hover:bg-slate-100 px-2 py-1 rounded transition-colors cursor-pointer"
          data-testid={`usage-count-${row.id}`}
        >
          <span className="text-blue-600 font-medium">{row.times_used || 0}</span>
          {row.max_uses && <span className="text-slate-400">/{row.max_uses}</span>}
          <Eye className="h-3 w-3 inline ml-1 text-slate-400" />
        </button>
      )
    },
    {
      key: 'validity',
      label: 'Validity',
      render: (row) => {
        if (!row.valid_from && !row.valid_until) {
          return <span className="text-slate-400 text-sm">No limit</span>;
        }
        return (
          <div className="text-sm">
            {row.valid_from && <p>From: {new Date(row.valid_from).toLocaleDateString()}</p>}
            {row.valid_until && <p>Until: {new Date(row.valid_until).toLocaleDateString()}</p>}
          </div>
        );
      }
    },
    {
      key: 'is_active',
      label: 'Status',
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: [
        { value: 'true', label: 'Active' },
        { value: 'false', label: 'Inactive' }
      ],
      render: (row) => (
        <Badge className={row.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}>
          {row.is_active ? 'Active' : 'Inactive'}
        </Badge>
      )
    }
  ];

  const actions = (row) => (
    <div className="flex items-center justify-end gap-2">
      <Button
        variant="ghost"
        size="icon"
        onClick={() => handleEdit(row)}
        data-testid={`edit-discount-${row.id}`}
      >
        <Pencil className="h-4 w-4" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        onClick={() => handleDelete(row)}
        className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
        data-testid={`delete-discount-${row.id}`}
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  );

  if (loading) {
    return (
      <DashboardLayout title="Discounts">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Discounts">
      <div className="space-y-6" data-testid="discounts-page">
        <div className="space-y-3">
          <p className="text-slate-600">Manage subscription discounts</p>
          <div className="flex flex-wrap items-center gap-2">
          <Button 
            className="bg-slate-900 hover:bg-slate-800" 
            onClick={() => { resetForm(); setDialogOpen(true); }}
            data-testid="add-discount-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Discount
          </Button>
          </div>
        </div>

        <Card className="border border-slate-200">
          <CardContent className="p-4">
            <DataTable
              data={discounts}
              columns={columns}
              actions={actions}
              emptyIcon={Tag}
              emptyMessage="No discounts created yet. Create your first discount!"
              searchable={true}
              searchFields={['name', 'description']}
              testIdPrefix="discounts"
            />
          </CardContent>
        </Card>

        {/* Add/Edit Discount Dialog */}
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl flex items-center gap-2">
                <Tag className="h-5 w-5 text-blue-500" />
                {editingDiscount ? 'Edit Discount' : 'Create New Discount'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Discount Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Early Bird Special"
                    required
                    data-testid="discount-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Unique Code</Label>
                  <Input
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    placeholder="e.g., SUMMER20"
                    className="font-mono uppercase"
                    data-testid="discount-code-input"
                  />
                  <p className="text-xs text-slate-500">Optional unique code for tracking</p>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Optional description..."
                  rows={2}
                  data-testid="discount-description-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Discount Type *</Label>
                  <Select
                    value={formData.discount_type}
                    onValueChange={(value) => setFormData({ ...formData, discount_type: value })}
                  >
                    <SelectTrigger data-testid="discount-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percentage">
                        <span className="flex items-center gap-2">
                          <Percent className="h-4 w-4" /> Percentage
                        </span>
                      </SelectItem>
                      <SelectItem value="fixed">
                        <span className="flex items-center gap-2">
                          <DollarSign className="h-4 w-4" /> Fixed Amount
                        </span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Value *</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                      {formData.discount_type === 'percentage' ? '%' : '$'}
                    </span>
                    <Input
                      type="number"
                      step={formData.discount_type === 'percentage' ? '1' : '0.01'}
                      min="0"
                      max={formData.discount_type === 'percentage' ? '100' : undefined}
                      value={formData.value}
                      onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                      className="pl-8"
                      required
                      data-testid="discount-value-input"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Application *</Label>
                <Select
                  value={formData.application}
                  onValueChange={(value) => setFormData({ ...formData, application: value })}
                >
                  <SelectTrigger data-testid="discount-application-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="one_time">One-Time (applies to first invoice only)</SelectItem>
                    <SelectItem value="recurring">Recurring (applies to every invoice)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Valid From</Label>
                  <Input
                    type="date"
                    value={formData.valid_from}
                    onChange={(e) => setFormData({ ...formData, valid_from: e.target.value })}
                    data-testid="discount-valid-from-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Valid Until</Label>
                  <Input
                    type="date"
                    value={formData.valid_until}
                    onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })}
                    data-testid="discount-valid-until-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Max Uses</Label>
                  <Input
                    type="number"
                    min="1"
                    value={formData.max_uses}
                    onChange={(e) => setFormData({ ...formData, max_uses: e.target.value })}
                    placeholder="Unlimited"
                    data-testid="discount-max-uses-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Min Enrollment Months</Label>
                  <Input
                    type="number"
                    min="1"
                    value={formData.min_enrollment_months}
                    onChange={(e) => setFormData({ ...formData, min_enrollment_months: e.target.value })}
                    placeholder="No minimum"
                    data-testid="discount-min-months-input"
                  />
                </div>
              </div>

              {/* Program Restrictions */}
              <div className="space-y-2">
                <Label>Applicable Programs (leave empty for all)</Label>
                <div className="border rounded-lg p-3 max-h-32 overflow-y-auto space-y-2 bg-slate-50">
                  {programs.filter(p => p.is_active).map(program => (
                    <label key={program.id} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.applicable_programs?.includes(program.id) || false}
                        onChange={() => toggleProgramSelection(program.id)}
                        className="w-4 h-4 rounded border-slate-300 text-blue-600"
                      />
                      <span className="text-sm">{program.name}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex items-center justify-between pt-2 border-t">
                <Label>Active</Label>
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                  data-testid="discount-active-switch"
                />
              </div>

              <DialogFooter className="pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-blue-500 hover:bg-blue-600" data-testid="save-discount-btn">
                  {editingDiscount ? 'Update Discount' : 'Create Discount'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="font-heading text-xl text-rose-600">
                Delete Discount
              </AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete <strong>"{discountToDelete?.name}"</strong>? 
                This action cannot be undone.
                {discountToDelete?.times_used > 0 && (
                  <span className="block mt-2 text-amber-600">
                    Note: This discount has been used {discountToDelete.times_used} time(s).
                  </span>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="delete-discount-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction 
                onClick={confirmDelete}
                className="bg-rose-600 hover:bg-rose-700"
                data-testid="delete-discount-confirm"
              >
                Delete Discount
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Usage History Dialog */}
        <Dialog open={usageDialogOpen} onOpenChange={setUsageDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl flex items-center gap-2">
                <Eye className="h-5 w-5 text-blue-500" />
                Discount Usage History
              </DialogTitle>
              {selectedDiscountForUsage && (
                <div className="mt-2 p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">{selectedDiscountForUsage.name}</p>
                      {selectedDiscountForUsage.code && (
                        <p className="text-sm text-slate-500 font-mono">Code: {selectedDiscountForUsage.code}</p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-blue-600">
                        {selectedDiscountForUsage.discount_type === 'percentage' 
                          ? `${selectedDiscountForUsage.value}% off` 
                          : `${cs()}${selectedDiscountForUsage.value} off`}
                      </p>
                      <p className="text-sm text-slate-500">
                        Used {selectedDiscountForUsage.times_used || 0} time(s)
                        {selectedDiscountForUsage.max_uses && ` of ${selectedDiscountForUsage.max_uses} max`}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </DialogHeader>
            
            <div className="mt-4">
              {loadingUsage ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-4 border-blue-500 border-t-transparent"></div>
                </div>
              ) : usageRecords.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <Tag className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                  <p>No usage records yet</p>
                  <p className="text-sm mt-1">Usage details will appear here when this discount is applied.</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date Applied</TableHead>
                      <TableHead>Parent</TableHead>
                      <TableHead>Player</TableHead>
                      <TableHead>Program</TableHead>
                      <TableHead>Class</TableHead>
                      <TableHead className="text-right">Original</TableHead>
                      <TableHead className="text-right">Discount</TableHead>
                      <TableHead className="text-right">Final</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {usageRecords.map((record) => (
                      <TableRow key={record.id}>
                        <TableCell className="text-sm">
                          {new Date(record.applied_at).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{record.parent_name || '-'}</p>
                            {record.parent_email && (
                              <p className="text-xs text-slate-500">{record.parent_email}</p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{record.player_name || '-'}</TableCell>
                        <TableCell>{record.program_name || '-'}</TableCell>
                        <TableCell>{record.class_name || '-'}</TableCell>
                        <TableCell className="text-right font-mono text-slate-600">
                          {cs()}{record.original_amount?.toFixed(2)}
                        </TableCell>
                        <TableCell className="text-right font-mono text-green-600">
                          -{cs()}{record.amount_saved?.toFixed(2)}
                        </TableCell>
                        <TableCell className="text-right font-mono font-medium">
                          {cs()}{record.final_amount?.toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              
              {usageRecords.length > 0 && (
                <div className="mt-4 p-3 bg-slate-50 rounded-lg flex justify-between items-center">
                  <span className="text-sm text-slate-600">
                    Total uses: <strong>{usageRecords.length}</strong>
                  </span>
                  <span className="text-sm text-green-600 font-medium">
                    Total savings: {cs()}{usageRecords.reduce((sum, r) => sum + (r.amount_saved || 0), 0).toFixed(2)}
                  </span>
                </div>
              )}
            </div>

            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={() => setUsageDialogOpen(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default Discounts;
